/**
 * Pure timesheet aggregation: turns raw time entries into billable rows grouped
 * by project over a date range. No DB / UI access so it's unit-testable and
 * reusable by the export adapter.
 */
import type { TimesheetRow } from "@time-tracker/shared";
import { DEFAULT_ROUNDING, roundDurationMs, type RoundingConfig } from "./rounding";

const MS_IN_HOUR = 3_600_000;

export const GRANULARITIES = ["day", "week", "month"] as const;
export type Granularity = (typeof GRANULARITIES)[number];

/** Minimal shape we need from a time_entry to aggregate it. */
interface TimesheetEntry {
  project_id: string | null;
  start_time: string | null;
  end_time: string | null;
  /** Sealed billing version (rate-sealing): the rate frozen at creation time. */
  project_version_id?: string | null;
}

/** Effective billing for one entry (rate-sealing resolution). */
export interface EntryRate {
  rate: number;
  currency: string | null;
}

/** Project metadata used to label rows and resolve rates. */
interface TimesheetProject {
  id: string;
  name: string | null;
  code: string | null;
}

const PAD = 2;
const pad = (n: number) => String(n).padStart(PAD, "0");

/** Local YYYY-MM-DD for an ISO timestamp. */
export function isoToLocalDay(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Local YYYY-MM (month bucket). */
function isoToLocalMonth(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
}

const DAYS_IN_WEEK = 7;
const MS_IN_DAY = 86_400_000;

/** ISO-week-ish label: the Monday of the entry's week (YYYY-MM-DD). */
function isoToWeekStart(iso: string): string {
  const d = new Date(iso);
  d.setHours(0, 0, 0, 0);
  // getDay(): 0=Sun..6=Sat → shift so Monday is the week start.
  const dow = (d.getDay() + (DAYS_IN_WEEK - 1)) % DAYS_IN_WEEK;
  const monday = new Date(d.getTime() - dow * MS_IN_DAY);
  return isoToLocalDay(monday.toISOString());
}

function bucketLabel(iso: string, granularity: Granularity): string {
  if (granularity === "week") {
    return isoToWeekStart(iso);
  }
  if (granularity === "month") {
    return isoToLocalMonth(iso);
  }
  return isoToLocalDay(iso);
}

/** True when `iso` is within [startMs, endMs] (inclusive). */
function inRange(iso: string, startMs: number, endMs: number): boolean {
  const t = new Date(iso).getTime();
  return t >= startMs && t <= endMs;
}

interface AggregateOptions {
  entries: readonly TimesheetEntry[];
  projects: readonly TimesheetProject[];
  /** Inclusive range bounds (ISO). Entries are bucketed by start_time. */
  rangeStart: string;
  rangeEnd: string;
  granularity: Granularity;
  /** Resolves the hourly rate for a project id (localStorage fallback path). */
  resolveRate: (projectId: string | null) => number;
  /**
   * Per-entry billing resolution (rate-sealing): prefer the SEALED version rate,
   * then the synced project rate, then localStorage. When provided it wins over
   * {@link resolveRate}, so historical blocks keep the rate they were billed at
   * even after the project's rate changes. When omitted, falls back to
   * {@link resolveRate} (legacy behaviour).
   */
  resolveEntryRate?: (entry: TimesheetEntry) => EntryRate;
  /**
   * Billing rounding (Toggl-style), applied to EACH entry's duration before the
   * rate: two 10' blocks rounded up to 15' bill 30', not 20' — per-entry, never
   * per aggregated bucket. Omitted / mode "none" → exact durations.
   */
  rounding?: RoundingConfig;
  /** Localized label for entries with no project (caller passes a translated string). */
  noProjectLabel: string;
}

/**
 * Aggregates entries into one {@link TimesheetRow} per (project × period bucket).
 * Running entries (no end_time) are clamped to "now". Rows are sorted by period
 * then project name.
 */
export function aggregateTimesheet(opts: AggregateOptions): TimesheetRow[] {
  const {
    entries,
    projects,
    rangeStart,
    rangeEnd,
    granularity,
    resolveRate,
    resolveEntryRate,
    rounding = DEFAULT_ROUNDING,
    noProjectLabel,
  } = opts;
  const startMs = new Date(rangeStart).getTime();
  const endMs = new Date(rangeEnd).getTime();
  const projectById = new Map(projects.map((p) => [p.id, p]));

  // key = `${projectId}|${period}` → accumulated hours + billed amount.
  // amount is summed PER ENTRY so blocks billed at different (sealed) rates keep
  // their own rate; the row's displayed rate is the blended amount/hours.
  const buckets = new Map<
    string,
    {
      projectId: string | null;
      period: string;
      hours: number;
      amount: number;
      currency: string | null;
    }
  >();

  for (const entry of entries) {
    if (!entry.start_time || !inRange(entry.start_time, startMs, endMs)) {
      continue;
    }
    const start = new Date(entry.start_time).getTime();
    const end = entry.end_time ? new Date(entry.end_time).getTime() : Date.now();
    if (end <= start) {
      continue;
    }
    const hours = roundDurationMs(end - start, rounding) / MS_IN_HOUR;
    const period = bucketLabel(entry.start_time, granularity);
    const resolved: EntryRate = resolveEntryRate
      ? resolveEntryRate(entry)
      : { rate: resolveRate(entry.project_id), currency: null };
    const amount = hours * resolved.rate;
    const key = `${entry.project_id ?? ""}|${period}`;
    const existing = buckets.get(key);
    if (existing) {
      existing.hours += hours;
      existing.amount += amount;
      existing.currency ??= resolved.currency;
    } else {
      buckets.set(key, {
        projectId: entry.project_id,
        period,
        hours,
        amount,
        currency: resolved.currency,
      });
    }
  }

  const rows: TimesheetRow[] = [];
  for (const bucket of buckets.values()) {
    const proj = bucket.projectId ? projectById.get(bucket.projectId) : undefined;
    const rate = bucket.hours > 0 ? bucket.amount / bucket.hours : 0;
    rows.push({
      project: proj?.name ?? noProjectLabel,
      period: bucket.period,
      hours: bucket.hours,
      rate,
      amount: bucket.amount,
      currency: bucket.currency,
    });
  }

  return rows.toSorted((a, b) => {
    if (a.period !== b.period) {
      return a.period < b.period ? -1 : 1;
    }
    return a.project.localeCompare(b.project);
  });
}
